import React, { useState, useEffect } from 'react';
import { Box, Typography, FormControl, Select, MenuItem, TextField, Card, CardContent, Chip } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);

import { employeesApi, projectsApi } from '../../../../../../../../services/api';
import LoadingSpinner from '../../../../../../../common/LoadingSpinner';
import { Field, Label } from '../../../../../../../common/Modal';
import EmployeeSearch from '../../../../../../../common/EmployeeSearch';
import ProjectSearch from '../../../../../../../common/ProjectSearch';
import EngagementTypeSelector from '../../../../../../../common/EngagementTypeSelector';
import { EmployeeAvatar } from '../../../../../../../common/EmployeeAvatar';
import { getStatusColor, getStatusTextColor } from '../../../../../../../../constants/employeeStatuses';
import { PROJECT_PURPOSE_TYPES } from '../../../../../../../../constants/projectPurposes';
import { EMPLOYEE_ENGAGEMENT_TYPES } from '../../../../../../../../constants/employeeEngagementTypes';
import EmptyState from '../../../../../../../common/EmptyState';

const Assignment = ({
  selectedEmployee,
  setSelectedEmployee,
  selectedEngagementType,
  setSelectedEngagementType,
  startDate,
  setStartDate,
  projectId,
  employeeId,
  onProjectSelect,
  selectedDestinationProject,
  disabledEngagementTypeCodes = [],
  // Opt-ins used by the Bench Candidates assign dialog (transfer-mode layout with a
  // preselected individual): restrict the project picker to one purpose, let the
  // engagement type be chosen (transfer mode normally keeps the current one), and
  // name the project field.
  projectPurpose = null,
  engagementTypeEditable = false,
  projectLabel = null,
  showProjectCode = false
}) => {
  const [employeeProjects, setEmployeeProjects] = useState([]);
  const [isLoadingEmployeeProjects, setIsLoadingEmployeeProjects] = useState(false);
  const [isScrolledToBottom, setIsScrolledToBottom] = useState(false);
  const [selectedProject, setSelectedProject] = useState(selectedDestinationProject);

  useEffect(() => {
    // Fetch project by ID if projectId is provided
    if (projectId) {
      fetchProjectById(projectId);
    }
  }, [projectId]);

  useEffect(() => {
    if (employeeId) {
      // Fetch employee by ID when employeeId is provided
      fetchEmployeeById(employeeId);
    }
  }, [employeeId]);

  useEffect(() => {
    // Only fetch employee projects if:
    // 1. We have a selected employee
    // 2. We're NOT in transfer mode (employeeId is not provided)
    // In transfer mode, fetchEmployeeById handles the project fetching
    if (selectedEmployee && !employeeId) {
      fetchEmployeeProjects(selectedEmployee.id);
    }
  }, [selectedEmployee]);

  useEffect(() => {
    if (selectedDestinationProject) {
      setSelectedProject(selectedDestinationProject);
    }
  }, [selectedDestinationProject]);

  const fetchProjectById = async (projectId) => {
    try {
      const project = await projectsApi.getProjectById(projectId);
      if (project) {
        // Create project object with purpose information for ProjectSearch
        const projectWithPurpose = {
          id: project.id,
          name: project.name,
          projectPurpose: project.projectPurpose ? {
            id: project.projectPurpose.id,
            code: project.projectPurpose.code,
            name: project.projectPurpose.name
          } : null
        };
        
        setSelectedProject(projectWithPurpose);
        
        // Notify parent component if callback is provided
        if (onProjectSelect) {
          onProjectSelect(projectWithPurpose);
        }
      }
    } catch (error) {
      console.error('fetchProjectById error');
    }
  };

  const fetchEmployeeById = async (employeeId) => {
    try {
      const employee = await employeesApi.getEmployeeById(employeeId);
      if (employee) {
        // Set the employee (this will trigger useEffect but we'll handle it there)
        setSelectedEmployee(employee);
        
        // Fetch projects with the employee data to avoid duplicate API call
        await fetchEmployeeProjects(employee.id, employee);
      }
    } catch (error) {
      console.error('fetchEmployeeById error');
    }
  };

  const fetchEmployeeProjects = async (employeeId, employeeData = null) => {
    try {
      setIsLoadingEmployeeProjects(true);
      
      // Use provided employeeData or fetch it if not provided
      const employee = employeeData || await employeesApi.getEmployeeById(employeeId);
      
      const activeProjects = employee.projects?.filter(project => 
        project.employeeData?.projectStatus !== 'removed'
      ) || [];
      
      setEmployeeProjects(activeProjects);
      
      // Don't auto-select employee's current project in attach mode
      // The destination project should be set via selectedDestinationProject prop or projectId
      
    } catch (error) {
      console.error('fetchEmployeeProjects error');
      setEmployeeProjects([]);
    } finally {
      setIsLoadingEmployeeProjects(false);
    }
  };

  const handleEmployeeChange = (newEmployee) => {
    setSelectedEmployee(newEmployee);
    
    // Only reset selected project if we're NOT in attach mode (no employeeId or projectId)
    // In attach mode, we want to keep the destination project selected
    if (!employeeId && !projectId) {
      setSelectedProject(null);
      if (onProjectSelect) {
        onProjectSelect(null);
      }
    }
    
    if (!newEmployee) {
      setEmployeeProjects([]);
    }
    // Don't call fetchEmployeeProjects here - let the useEffect handle it
  };

  const handleScroll = (e) => {
    const element = e.target;
    const reachedBottom = Math.abs(element.scrollHeight - element.clientHeight - element.scrollTop) < 1;
    setIsScrolledToBottom(reachedBottom);
  };

  const handleProjectChange = (project) => {
    setSelectedProject(project);
    if (onProjectSelect) {
      onProjectSelect(project);
    }
  };

  // Determine the mode:
  // - Attach mode: projectId provided → project is read-only (attaching employee TO a specific project)
  // - Transfer mode: employeeId provided → project is selectable (transferring employee TO any project)
  // - Normal mode: neither provided → both employee and project are selectable
  const isAttachMode = !!projectId;
  const isTransferMode = !!employeeId && !projectId;

  return (
    <Box>
      <Field>
        <Typography variant="subtitle1" gutterBottom>{employeeId ? 'Individual' : 'Select Individual'}</Typography>
        {employeeId ? (
          // Show read-only employee info when employeeId is provided (transfer mode)
          selectedEmployee ? (
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 1,
              p: 2,
              border: '1px solid #E2E8F0',
              borderRadius: '4px',
              backgroundColor: '#F8FAFC'
            }}>
              <EmployeeAvatar employee={selectedEmployee} size={32} />
              <Box>
                <Typography sx={{ fontWeight: 500 }}>{selectedEmployee.fullName}</Typography>
                <Typography variant="caption" color="textSecondary">
                  {selectedEmployee.email}
                </Typography>
              </Box>
            </Box>
          ) : (
            <LoadingSpinner size={24} />
          )
        ) : (
          <EmployeeSearch
            value={selectedEmployee}
            onChange={handleEmployeeChange}
            label=""
            placeholder="Search individual..."
            width={400}
            sx={{ width: '100%' }}
            showSelectedAvatar={true}
          />
        )}
      </Field>

      {(selectedEmployee || employeeId) && (
        <>
          <Field>
            <Typography variant="subtitle1" gutterBottom>{projectLabel || (isTransferMode ? 'Destination Project' : 'Project')}</Typography>
            {isAttachMode ? (
              // Attach mode: Show read-only project (projectId provided - attaching TO this project)
              selectedProject ? (
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'space-between',
                  gap: 1,
                  p: 2,
                  border: '1px solid #E2E8F0',
                  borderRadius: '4px',
                  backgroundColor: '#F8FAFC'
                }}>
                  <Typography sx={{ fontWeight: 500 }}>{selectedProject.name}</Typography>
                  {selectedProject.projectPurpose && (
                    <Chip 
                      label={selectedProject.projectPurpose.name} 
                      size="small" 
                      sx={{ 
                        height: 20, 
                        fontSize: '0.7rem',
                        backgroundColor: '#e3f2fd',
                        color: '#1565c0'
                      }}
                    />
                  )}
                </Box>
              ) : (
                <LoadingSpinner size={24} />
              )
            ) : (
              // Transfer mode OR Normal mode: Show interactive ProjectSearch
              <ProjectSearch
                value={selectedProject}
                onChange={handleProjectChange}
                employeeId={selectedEmployee?.id}
                projectPurpose={projectPurpose}
                showProjectCode={showProjectCode}
                placeholder={isTransferMode ? "Search destination project..." : "Search destination project..."}
                width={400}
                sx={{ width: '100%' }}
                showAssignmentIndicators={true}
              />
            )}
          </Field>

          <Box sx={{ 
            display: 'grid', 
            gridTemplateColumns: '1fr 1fr', 
            gap: 2,
            '& .MuiFormControl-root': { minWidth: '100%' }
          }}>
            <Field>
              <Typography variant="subtitle1" gutterBottom>Engagement Type</Typography>
              <EngagementTypeSelector
                disabled={isTransferMode && !engagementTypeEditable}
                value={selectedEngagementType}
                onChange={setSelectedEngagementType}
                placeholder="Select an engagement type"
                disabledEngagementTypeCodes={
                  disabledEngagementTypeCodes.length > 0
                    ? disabledEngagementTypeCodes
                    : selectedProject?.projectPurpose?.code !== PROJECT_PURPOSE_TYPES.DEFAULT_INTERNAL
                      ? [EMPLOYEE_ENGAGEMENT_TYPES.FULL_TIME]
                      : []
                }
              />
            </Field>

            <Field>
              <Typography variant="subtitle1" gutterBottom>Start Date</Typography>
              <DatePicker
                value={startDate}
                onChange={(newValue) => {
                  if (newValue && typeof newValue.isValid === 'function' && newValue.isValid()) {
                    setStartDate(newValue);
                  } else if (newValue === null) {
                    setStartDate(null);
                  }
                }}
                format="DD/MM/YYYY"
                slotProps={{
                  textField: {
                    fullWidth: true,
                    placeholder: "When will the individual start on this project?",
                    size: "medium",
                    required: true,
                    inputProps: {
                      placeholder: 'DD/MM/YYYY'
                    }
                  }
                }}
              />
            </Field>
          </Box>

          {selectedEmployee && (
            <Field>
              <Typography variant="subtitle1" gutterBottom>
                {employeeProjects.length > 0 
                  ? `Found ${employeeProjects.length} ${employeeProjects.length === 1 ? 'Project' : 'Projects'} for ${selectedEmployee?.fullName}`
                  : `Projects for ${selectedEmployee?.fullName}`
                }
              </Typography>
              {isLoadingEmployeeProjects ? (
                <Box sx={{ p: 2, textAlign: 'center' }}>
                  <LoadingSpinner size={24} />
                </Box>
              ) : employeeProjects.length === 0 ? (
                <EmptyState
                  title="No Active Projects Found"
                  description="This individual is not assigned to any active projects."
                />
              ) : (
                <Box sx={{ 
                  position: 'relative',
                  maxHeight: '250px'
                }}>
                  <Box 
                    onScroll={handleScroll}
                    sx={{ 
                      overflowY: 'scroll',
                      maxHeight: '250px',
                      pr: 1,
                      pb: 2
                    }}
                  >
                    {employeeProjects.map(project => (
                      <Card 
                        key={project.id} 
                        sx={{ 
                          backgroundColor: '#f8f9fa',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          height: '220px',
                          width: '100%',
                          mb: 2
                        }}
                      >
                        <CardContent sx={{ height: '100%', p: 2 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                              {project.name}
                            </Typography>
                            {project.employeeData?.projectStatus && (
                              <Box
                                sx={{
                                  display: 'inline-block',
                                  padding: '4px 12px',
                                  borderRadius: '16px',
                                  fontSize: '14px',
                                  fontWeight: '500',
                                  backgroundColor: getStatusColor(project.employeeData.projectStatus),
                                  color: getStatusTextColor(project.employeeData.projectStatus),
                                  ml: 1
                                }}
                              >
                                {project.employeeData.projectStatus.charAt(0).toUpperCase() + project.employeeData.projectStatus.slice(1)}
                              </Box>
                            )}
                          </Box>
                          {project.employeeData && (
                            <Box sx={{ 
                              display: 'flex', 
                              flexDirection: 'column', 
                              gap: 0.5,
                              color: '#64748B',
                              fontSize: '13px'
                            }}>
                              {project.employeeData.timeInProject && (
                                <div style={{ 
                                  marginBottom: '4px',
                                  color: '#64748B',
                                  fontSize: '13px',
                                  fontStyle: 'italic'
                                }}>
                                  {project.employeeData.timeInProject.years === 0 && 
                                   project.employeeData.timeInProject.months === 0 && 
                                   project.employeeData.timeInProject.days === 0 
                                    ? 'Not started yet'
                                    : (
                                      <>
                                        {project.employeeData.timeInProject.years > 0 && `${project.employeeData.timeInProject.years} year${project.employeeData.timeInProject.years !== 1 ? 's' : ''}`}
                                        {project.employeeData.timeInProject.years > 0 && project.employeeData.timeInProject.months > 0 && ', '}
                                        {project.employeeData.timeInProject.months > 0 && `${project.employeeData.timeInProject.months} month${project.employeeData.timeInProject.months !== 1 ? 's' : ''}`}
                                        {((project.employeeData.timeInProject.years > 0 || project.employeeData.timeInProject.months > 0) && project.employeeData.timeInProject.days > 0) && ', '}
                                        {project.employeeData.timeInProject.days > 0 && `${project.employeeData.timeInProject.days} day${project.employeeData.timeInProject.days !== 1 ? 's' : ''}`}
                                        {' on the Project'}
                                      </>
                                    )}
                                </div>
                              )}
                              {project.employeeData.team && (
                                <Typography variant="body2" color="text.secondary" noWrap>
                                  <strong>Team:</strong> {project.employeeData.team.name}
                                </Typography>
                              )}
                              {project.employeeData.engagementType && (
                                <Typography variant="body2" color="text.secondary" noWrap>
                                  <strong>Engagement:</strong> {project.employeeData.engagementType.name}
                                </Typography>
                              )}
                              {project.employeeData.roles && project.employeeData.roles.length > 0 && (
                                <Typography variant="body2" color="text.secondary" noWrap>
                                  <strong>Roles:</strong> {project.employeeData.roles.map(role => role.label).join(', ')}
                                </Typography>
                              )}
                              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                <Typography variant="body2" color="text.secondary" noWrap>
                                  <strong>Start Date:</strong> {project.employeeData.startDate ? dayjs.utc(project.employeeData.startDate).format('MMM D, YYYY') : 'Not set'}
                                </Typography>
                                {project.employeeData.endDate && (
                                  <Typography variant="body2" color="text.secondary" noWrap>
                                    <strong>End Date:</strong> {dayjs.utc(project.employeeData.endDate).format('MMM D, YYYY')}
                                  </Typography>
                                )}
                              </Box>
                            </Box>
                          )}
                        </CardContent>
                      </Card>
                    ))}
                  </Box>
                  {employeeProjects.length > 1 && !isScrolledToBottom && (
                    <Box sx={{
                      position: 'absolute',
                      bottom: 0,
                      left: 0,
                      right: 0,
                      height: '30px',
                      background: 'linear-gradient(to bottom, rgba(255,255,255,0), rgba(255,255,255,1) 90%)',
                      pointerEvents: 'none',
                      boxShadow: '0 -8px 12px -4px rgba(0,0,0,0.1)',
                      '&:after': {
                        content: '"Scroll for more"',
                        position: 'absolute',
                        bottom: '-4px',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        color: '#94a3b8',
                        fontSize: '12px',
                        fontStyle: 'italic'
                      }
                    }} />
                  )}
                </Box>
              )}
            </Field>
          )}
        </>
      )}
    </Box>
  );
};

export default Assignment; 